import { html, nothing } from "lit-html";
import {
  CardTypes,
  CreditCardDisplay,
  PaymentDetails,
  PaymentType,
  RmsBookstoreAddress,
  VaultedCard
} from "../../features/checkout/shop-checkout.interface";
import store from '../../store/store';
import { css } from 'lit';
import { resetPaymentDetails, updatePoOrderNumber, updatePurchaseOrderApplied } from "../../features/checkout/shop-checkout";

/**
 * @returns template that renders billing address results.
 */
export function renderCompletedAddress(address: RmsBookstoreAddress) {
  return html`
    <address>
      <p class="mb-0" data-automation-id="billing-name">${address.firstName} ${address.lastName}</p>
      <p class="mb-0" data-automation-id="billing-address1">${address.address1}</p>
      <p class="mb-0" data-automation-id="billing-city-state-zipcode">${address.city}, ${address.state} ${address.zipCode}</p>
    </address>
  `;
}

export function renderBillingAddressErrorMessage() {
  return html`
    <span class="text-primary d-block pt-3">Billing address is needed before</span>
    <span class="text-primary d-block">proceeding</span>
  `;
}

/**
 * Renders the vaulted credit card information.
 *
 * @param creditCardDisplayData
 */
export function renderCreditCardDisplay(creditCardDisplayData: CreditCardDisplay) {
  return html`
    <div class="d-flex">
      ${creditCardDisplayData ? cardDisplay(creditCardDisplayData) : nothing}
    </div>
  `;
}

/**
 * Renders the remove link for the credit card.
 *
 * @param removeLinkClickEventHandler
 * @param isCurrentPayment
 */
export function renderCreditCardRemoveLink(removeLinkClickEventHandler?, isCurrentPayment = false) {
  return html`
    <div class="d-flex float-end">
      ${!isCurrentPayment && removeLinkClickEventHandler ? html`
        <a id="remove-card" href="" @click="${removeLinkClickEventHandler}">Remove</a>
      `: nothing}
    </div>
  `;
}

/**
 * Renders the expired badge for the vaulted card.
 *
 * @param vaultedCard
 */
export function renderExpiredVaultedCardInfo(vaultedCard: VaultedCard) {
  return vaultedCard.isExpired
    ? html`<span class="badge bg-danger">Expired</span>`
    : nothing
}

function cardDisplay(creditCardData) {
  const disabledAriaLabel = 'Disabled, credit card ending in ' + creditCardData.cardLast4 + ' is expired';

  return html`
    <div class="cardDisplayLogo border rounded">
      <img src="${getCardLogo(creditCardData.cardType)}" class="w-75 h-75" alt="${creditCardData.cardType}">
    </div>
    ${creditCardData?.isExpired ? html`
      <ul class="ms-2 list-unstyled mb-0" aria-label=${disabledAriaLabel} tabindex="0">
        <li class="text-nowrap">Ending... ${creditCardData.cardLast4}</li>
        <li class="text-nowrap">${creditCardData.expiryMonth} / ${creditCardData.expiryYear}</li>
      </ul>
    ` : html`
      <ul class="ms-2 list-unstyled mb-0">
        <li class="text-nowrap">Ending... ${creditCardData.cardLast4}</li>
        <li class="text-nowrap">${creditCardData.expiryMonth} / ${creditCardData.expiryYear}</li>
      </ul>
    `}
    </div>`;
}

export function renderPaypalDisplay(paymentDetails: PaymentDetails) {
  const paypalStyles = css`
    #paypal-data-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
    @media (min-width: 360px) {
      #paypal-data-container {
        flex-direction: row;
        align-items: center;
      }
    }
  `;

  return html`
    <style>
      ${paypalStyles}
    </style>
    <div id="paypal-data-container">
      <div class="border border-1 rounded me-3 p-2">
        <div class="paypalLogo"></div>
      </div>
      <div class="my-2">
        <p class="m-0">PayPal</p>
        <p class="m-0" data-automation-id="paypal-customer-email">${paymentDetails.paypalEmail}</p>
      </div>
    </div>
  `
}

/**
 * Checks if the given payment type is a 3rd party payment or not, based on the isThirdParty flag.
 *
 * @param paymentType - The type of payment to check.
 * @param isThirdParty - A flag indicating whether to check for 3rd party payment or not. Defaults to true.
 *
 * @returns a boolean indicating if the payment type is a 3rd party payment or not.
 */
export function check3rdPartyPaymentType(paymentType?: PaymentType, isThirdParty = true): boolean {
  if (!paymentType) {
    return !isThirdParty;
  }

  const thirdPartyPayments = [
    PaymentType.BRAINTREE_APPLE_PAY,
    PaymentType.BRAINTREE_GOOGLE_PAY,
    PaymentType.BRAINTREE_PAYPAL
  ];

  return isThirdParty ? thirdPartyPayments.includes(paymentType) : !thirdPartyPayments.includes(paymentType);
}

/**
 * Displays card details for 3rd party methods that send back card information.
 *
 * @param billingAddress
 * @param details
 */
export function render3rdPartyPaymentCardInfo(billingAddress: RmsBookstoreAddress, details?: PaymentDetails) {
  const paymentData = {
    [PaymentType.BRAINTREE_APPLE_PAY]: {automationPrefix: 'apple-pay', logoClass: 'applePayLogo'},
    [PaymentType.BRAINTREE_GOOGLE_PAY]: {automationPrefix: 'gpay', logoClass: 'googlePayLogo'},
  };

  if (!details?.paymentType || !paymentData[details.paymentType]) {
    return html``;
  }

  return html`
    <div class="justify-content-between col-12 col-sm-8">
      <div class="d-flex flex-column">
        <div class="d-flex align-items-center">
          <div class="${paymentData[details.paymentType]?.logoClass ?? ''} py-1 me-2"></div>
          <div class="vr fs-2 mx-2"></div>
          <div class="cardDisplayLogo">
            <img
              src="${getCardLogo(details.thirdPartyDetails!.cardType)}"
              data-automation-id="${paymentData[details.paymentType]?.automationPrefix ?? ''}-card-type"
              class="w-75 h-75"
              alt="${details.thirdPartyDetails!.cardType}"
            />
          </div>
          <span
            class="ms-1 text-muted fs-5"
            data-automation-id="${paymentData[details.paymentType]?.automationPrefix ?? ''}-last-four"
          >•••• ${details.thirdPartyDetails?.lastDigits}</span>
        </div>
        ${billingAddress ? html`
          <div id="billing-address-details-container" class="my-2">
            <h3 class="fs-6"><b>Billing Address</b></h3>
            ${renderCompletedAddress(billingAddress)}
          </div>
        ` : nothing}
      </div>
    </div>
  `
}

export function renderVenmoDisplay(userName: string) {
  return html`
    <div class=" border border-1 rounded me-3 p-1 mb-3" >
      <div class="venmoLogo mx-2 mb-3"></div>
      </div>
      <div>
      <p class="m-0">Venmo</p>
      <p class="m-0" tabindex="-1" id="venmoUserName"> ${userName}</p>
      <span class="visually-hidden" aria-live="polite"> Venmo payment successfully authorized</span>
      </div>
  `
}

export function getCardLogo(cardType: string) {
  const logoUrl = (logo) => `/uxt/static/external/misc/${logo}`
  return logoUrl({
    [CardTypes.AMEX]: 'logo-am-ex-inline.svg',
    [CardTypes.APPLE_PAY]: 'logo-apple-pay.svg',
    [CardTypes.DISCOVER]: 'logo-discover.svg',
    [CardTypes.GOOGLE_PAY]: 'logo-google-pay.svg',
    [CardTypes.MASTERCARD]: 'logo-mastercard.svg',
    [CardTypes.PAYPAL]: 'logo-paypal.svg',
    [CardTypes.VENMO]: 'logo-venmo.svg',
    [CardTypes.VISA]: 'logo-visa.svg'
  }[cardType]);
}

/**
 * Verifies if Apple Pay is the selected payment method.
 *
 * @param paymentDetail
 */
export function isApplePaymentMethod(paymentDetail?: PaymentDetails): boolean {
  return paymentDetail?.paymentType === PaymentType.BRAINTREE_APPLE_PAY;
}

/**
 * Verifies if Apple Pay details are valid.
 *
 * @param paymentDetail
 */
export function isValidApplePayDetail(paymentDetail?: PaymentDetails): boolean {
  return !!paymentDetail && isApplePaymentMethod(paymentDetail) && !!paymentDetail.paymentNonce
}

export function isPaypalPaymentMethod(paymentDetail?: PaymentDetails) {
  return paymentDetail?.paymentType === PaymentType.BRAINTREE_PAYPAL;
}

export function isValidPaypalDetail(paymentDetail?: PaymentDetails) {
  return paymentDetail
    && isPaypalPaymentMethod(paymentDetail)
    && paymentDetail.paypalEmail
}

export function isGooglePaymentMethod(paymentDetail?: PaymentDetails) {
  return paymentDetail?.paymentType === PaymentType.BRAINTREE_GOOGLE_PAY;
}

export function isValidGooglePayDetail(paymentDetail?: PaymentDetails) {
  return paymentDetail
    && isGooglePaymentMethod(paymentDetail)
    && paymentDetail.paymentNonce
}

export function isValidVenmoDetail(paymentDetail?: PaymentDetails) {
  return paymentDetail
    && paymentDetail.paymentType === PaymentType.BRAINTREE_VENMO
    && paymentDetail.paymentNonce
}

/**
 * Verifies if billing address is valid and enforces a boolean response.
 *
 * @param billingAddress
 */
export function isValidBillingAddress(billingAddress?: RmsBookstoreAddress): boolean {
  return !!billingAddress && !!billingAddress.firstName && !!billingAddress.lastName && !!billingAddress.country && !!billingAddress.state && !!billingAddress.address1 && !!billingAddress.city && !!billingAddress.zipCode;
}

/**
 * To reset purchase order number, applied status and related payment details in the store.
 * This is needed when user removes purchase order payment method or switch to other payment method from purchase order after selecting it.
 */
export function resetPurchaseOrderNumber() {
  store.dispatch(updatePoOrderNumber(""));
  store.dispatch(updatePurchaseOrderApplied(false));
  store.dispatch(resetPaymentDetails("Purchase Order"));
}
